import { useLocalization } from '~/composables/_next/useLocalization';

type Attribute = {
  key: string;
  label: {
    en: string;
    fr: string;
    it: string;
  };
};

/**
 * @function useProductPropertiesAttributes
 * @description Hook to handle product properties attributes for beauty and wellness.
 * @returns {Object} An object containing the function to get the final attribute string.
 */
export function useProductPropertiesAttributes() {
  const { locale } = useLocalization();
  /**
   * Get the first two elements of an array.
   *
   * @param {Attribute[]} attributesArray - The array from which to extract the elements.
   * @returns {Attribute[]} - A new array containing the first two elements of the input array, or an empty array if the input array is empty or undefined.
   */
  function getFirstTwoAttributes(attributesArray: Attribute[]): Attribute[] {
    return attributesArray?.slice(0, 2) ?? [];
  }

  /**
   * Get the final attribute string based on beauty and wellness attributes.
   *
   * @param {Attribute[]} beauty - Array of beauty attributes.
   * @param {Attribute[]} wellness - Array of wellness attributes.
   * @returns {string} - The final attribute string.
   */
  function getFinalAttributeString(beauty: Attribute[] = [], wellness: Attribute[] = []): string {
    const beautyAttributes = getFirstTwoAttributes(beauty);
    const wellnessAttributes = getFirstTwoAttributes(wellness);

    if (beautyAttributes.length > 0 && wellnessAttributes.length > 0) {
      return `${beautyAttributes[0]?.label[locale] ?? ''} • ${wellnessAttributes[0]?.label[locale] ?? ''}`;
    }

    if (beautyAttributes.length > 1) {
      return `${beautyAttributes[0]?.label[locale] ?? ''} • ${beautyAttributes[1]?.label[locale] ?? ''}`;
    }

    if (wellnessAttributes.length > 1) {
      return `${wellnessAttributes[0]?.label[locale] ?? ''} • ${wellnessAttributes[1]?.label[locale] ?? ''}`;
    }

    return beautyAttributes[0]?.label[locale] ?? wellnessAttributes[0]?.label[locale] ?? '';
  }
  /**
   * Get the final attribute string from raw string arrays like ["Protecteur", "Nutritif"].
   *
   * @param {string[]} beauty - Array of beauty property strings.
   * @param {string[]} wellness - Array of wellness property strings.
   * @returns {string} - The formatted attribute string.
   */
  function getFinalAttributeFromStrings(beauty: string[] = [], wellness: string[] = []): string {
    const beautySlice = beauty?.slice(0, 2) ?? [];
    const wellnessSlice = wellness?.slice(0, 2) ?? [];

    if (beautySlice.length > 0 && wellnessSlice.length > 0) {
      return `${beautySlice[0] ?? ''} • ${wellnessSlice[0] ?? ''}`;
    }

    if (beautySlice.length > 1) {
      return `${beautySlice[0] ?? ''} • ${beautySlice[1] ?? ''}`;
    }

    if (wellnessSlice.length > 1) {
      return `${wellnessSlice[0] ?? ''} • ${wellnessSlice[1] ?? ''}`;
    }

    return beautySlice[0] ?? wellnessSlice[0] ?? '';
  }

  return {
    getFinalAttributeString,
    getFinalAttributeFromStrings,
  };
}
